import { Component, EventEmitter, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { ProgressSpinnerComponent } from '../../helper/anessa-library/anessa-library/projects/anessa-components/src/public-api';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import { UnitService } from '../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { AuthService } from '../../auth/auth.service';
import { AnessaTranslationService } from '../../helper/translation/anessa-translation.service';
import { CommunicationService } from '../../communication/communication.service';
import { ProjectCommunicationService } from '../../communication/project-comms.service';
import { ConfiguredToastrService } from '../../helper/toastr/configured-toastr.service';
import { DefaultAssumptionsService } from '../project-input/default-assumptions.service';
import { ValidationService } from '../../helper/validation/validation.service';
import { AttachedFilesComponent } from '../project-info/general/attached-files/attached-files.component';
import { EmailTemplateDialogComponent } from './email-template-dialog/email-template-dialog.component';
import { InputReviewDialogComponent } from './input-review-dialog/input-review-dialog.component';
import { ProgressPopupDialogComponent } from '../progress-sidebar/progress-popup/progress-popup.component';
import { SubmissionProgressDialogComponent } from './submission-progress-dialog/submission-progress-dialog.component';
import { MemberComponent } from '../project-info/general/members/member.component';
import { Project, ProjectInterface } from '../../models/project/project.model';
import { ProjectInfo, ProjectFile, ProjectMember } from '../../models/project/project-info.model';
import { ValidationSectionName } from '../../models/data-structures/project-validation';
import Utils from '../../util';
import { DeactivationDialogService } from '../../helper/can-deactivate/deactivation-dialog.service';
import { ProjectChecklistService } from '../project-info/checklist/checklist.service';
import { NgForm } from '@angular/forms';
import { ConfirmationDialogComponent } from '../../helper/confirmation-dialog-component/confirmation-dialog.component';
import { UserpilotReloader } from '../../helper/userpilot-reloader.component';
import { ChecklistTemplateDialogComponent } from '../project-info/checklist/checklist-list/checklist-template-dialog/checklist-template-dialog.component';

@Component({
  selector: 'app-initial-information',
  templateUrl: './project-initial-information.component.html',
  styleUrls: [
    './project-initial-information.component.scss',
  ],
  providers: [ProjectChecklistService]
})
export class ProjectInitialInformationComponent extends UserpilotReloader implements OnInit, OnDestroy {
  @ViewChild('form') form: NgForm;

  onDestroyNotifier = new EventEmitter<boolean>();
  trigger: string;

  projectInfo: ProjectInfo = new ProjectInfo();
  filesToDelete: ProjectFile[] = [];

  emailTemplate = `Dear Valued Client,

A new project has been created and you have been invited to contribute additional information.
Please follow the link below to log into your account and start entering your information.
`;
  resendEmailTemplate = `Dear Valued Client,

A project has been updated and you have been invited to contribute.
Please follow the link below to create an account and start entering your information.
`;
  projectLoaded = false;
  resultsLoaded = false;
  unitsLoaded = false;

  hasChecklistAccess = false;
  hasFormAccess = false;

  disableSaveButtons = false;
  isEdit = false;
  isInitialFormUser = false;
  showPage = false;
  showClearTitle = false;
  showProjectInput = false;
  showClearNotes = false;
  pageDirty = false;
  hideSectionForm = false;
  creatingNewTemplate = false;

  constructor(
    private checklistService: ProjectChecklistService,
    private dialog: MatDialog,
    private deactivationDialogService: DeactivationDialogService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private unitService: UnitService,
    private assumptionsService: DefaultAssumptionsService,
    private authService: AuthService,
    private commService: CommunicationService,
    private projectCommService: ProjectCommunicationService,
    private toastrService: ConfiguredToastrService,
    private validationService: ValidationService,
    public translationService: AnessaTranslationService,
  ) {
    super();
    this.checklistService.isProjectInput = true;
  }

  ngOnInit() {
    this.setTranslations();
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.setTranslations();
        this.trigger = this.translationService.getCurrentLang();
      });

    this.snackBar.openFromComponent(ProgressSpinnerComponent, {
      data: this.translationService.get('PROJECT.INITIAL.SNACKBAR'),
      panelClass: ['anessa-snack-bar-container'],
    });

    this.projectCommService.projectFullyLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe((loaded: boolean) => {
        this.projectLoaded = loaded;

        if (loaded) {
          this.projectCommService.resultsLoadedNotifier
            .pipe(takeUntil(this.onDestroyNotifier))
            .subscribe((loaded: boolean) => {
              this.resultsLoaded = loaded;
            });

          // Only attempt to load results if project has been created
          if (this.projectCommService.getProjectId() && !this.resultsLoaded) {
            this.projectCommService.loadResults(undefined, true);
          }

          this.setup();
        }
      });

    this.projectCommService.projectSavedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe((loaded: boolean) => {
        if (loaded) {
          this.projectInfo = this.projectCommService.getProjectInfo();
        }
      });

    this.unitService.unitsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe((loaded: boolean) => {
        this.unitsLoaded = loaded;
        this.setup();
      });
    if (!this.unitService.unitsLoadedNotifier.value) {
      this.unitService.loadData();
    }

    if (Utils.isInitialFormUser()) {
      this.isInitialFormUser = true;
      this.showProjectInput = true;
      this.setup();
    } else {
      if (!this.projectCommService.getProject()) {
        this.projectCommService.setProject(new Project());
        Utils.setProjectPermissions(true, true, true, true, false, false);
      } else {
        this.projectLoaded = true;
        this.projectCommService.projectFullyLoadedNotifier.next(true);
      }
      this.projectCommService.getProjectNames();
      this.setup();
    }
    this.hasChecklistAccess = Utils.hasInputListAccess();
    this.hasFormAccess = Utils.hasFormAccess();

    this.router.events
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(event => {
        if (this.router.url.includes('/create-template')) {
          this.creatingNewTemplate = true;
        } else {
          this.creatingNewTemplate = false;
        }
      });

    if (this.router.url.includes('/create-template')) {
      this.creatingNewTemplate = true;
    }
  }

  ngOnDestroy() {
    this.projectCommService.enableProjectSaveToDatabase();
    this.projectCommService.setIsProjectInput(false);
    // unload project to prevent the limited users from wandering around the actual software. Except when going to scoping results
    if (this.router.routerState.snapshot.url.indexOf('scoping-results') === -1) {
      this.projectCommService.unloadProject();
    }
    this.onDestroyNotifier.next(true);
    this.onDestroyNotifier.unsubscribe();
  }

  setTranslations() {
    this.emailTemplate = this.translationService.get('PROJECT.INITIAL.EMAIL_TEMPLATE');
  }

  setup() {
    this.projectCommService.setIsProjectInput(true);
    this.projectCommService.disableProjectSaveToDatabase();
    this.projectInfo = this.projectCommService.getProjectInfo();
    this.isEdit = this.projectCommService.getProjectId() !== '' && this.projectCommService.getProjectId() != null;
    if (this.projectInfo && !this.isEdit) {
      // Set up new project
      this.projectInfo.isDraftSubmission = true;
      this.projectInfo.isSubmittedProject = false;
      this.projectInfo.isApprovedProject = false;
      this.projectInfo.isNewProject = true;
      this.projectInfo.useGHG = true;
      this.projectCommService.setProjectInfo(this.projectInfo);
    }
    this.disableSaveButtons = this.projectInfo?.isSubmittedProject && this.isInitialFormUser;
    this.snackBar.dismiss();
    this.checkLoaded();
  }

  checkLoaded() {
    if (this.unitsLoaded && this.projectLoaded) {
      this.showPage = true;
    }
  }

  clearInput(property: string) {
    const newProjectInfo = new ProjectInfo();
    this.projectInfo[property] = newProjectInfo[property];
    this.changesMade();
  }

  onBack() {
    if (this.showProjectInput) {
      this.showProjectInput = false;
    } else {
      this.router.navigateByUrl('/project/open');
    }
  }

  onShowForm() {
    this.showProjectInput = true;
  }

  isDisableForm() {
    return !this.projectInfo.title || this.disableSaveButtons;
  }

  canDeactivate() {
    if ((this.pageDirty || this.form.form.dirty) && this.deactivationDialogService.getOpenDialogsLength() < 1 && !this.disableSaveButtons) {
      return this.deactivationDialogService.confirm(this.translationService.get('PROJECT.COMMON.DEACTIVATE_CONFIRM'));
    }
    return true;
  }

  private saveProjectToDatabase(project: Project, successMessage: string, callback?: Function) {
    this.disableSaveButtons = true;
    if (this.authService.isLoggedIn()) {
      if (this.isEdit) {
        // Save existing project
        this.commService.postRequest(project, `/wsasapi/project/saveproject/${project._id}?ifu=true`)
          .pipe(take(1))
          .pipe(takeUntil(this.onDestroyNotifier))
          .subscribe(data => {
            this.pageDirty = false;
            this.form.form.markAsPristine();
            const iProject: ProjectInterface = JSON.parse(JSON.stringify(data['obj']));
            this.projectCommService.setProject(new Project(iProject));
            if (successMessage) {
              this.toastrService.success(successMessage);
            }
            this.setup();
            this.removeFiles();
            if (callback) {
              callback(this.projectCommService.getProject());
            }
          });
      } else {
        // Create new project
        this.commService.postRequest(project, '/wsasapi/project')
          .pipe(take(1))
          .pipe(takeUntil(this.onDestroyNotifier))
          .subscribe(data => {
            this.pageDirty = false;
            this.form.form.markAsPristine();
            const iProject: ProjectInterface = JSON.parse(JSON.stringify(data['obj']));
            this.projectCommService.setProject(new Project(iProject));
            if (successMessage) {
              this.toastrService.success(successMessage);
            }
            Utils.setProjectPermissions(true, true, true, true, false, false);
            this.setup();
            if (callback) {
              callback(this.projectCommService.getProject());
            }
          });
      }
    }
  }

  private approveProject() {
    const projectId = this.projectCommService.getProjectId();
    this.commService.postRequest({}, `/wsasapi/project/${projectId}/approve`)
      .pipe(take(1))
      .subscribe(() => {
        this.toastrService.success(this.translationService.get('PROJECT.OPEN.APPROVE_SUCCESS'));
        this.projectCommService.enableProjectSaveToDatabase();
        this.projectCommService.setIsProjectInput(false);
        this.projectCommService.unloadProject();
        this.router.navigateByUrl('/project/open');
      });
  }

  onSend() {
    if (!this.isEdit && (!this.projectInfo.title || !this.projectCommService.checkUniqueProjectTitle(this.projectInfo.title))) {
      return;
    }

    let template = this.projectInfo.isSubmittedProject ? this.resendEmailTemplate : this.emailTemplate;
    let signature = '';
    if (Utils.hasInputListAccess()) {
      template = 'Dear Valued Client,\n\nYou have been invited to contribute to the project. Please follow the link below to log in or create your account to view.';
      signature = 'your anessa team\nsupport@anessa.com';
    }

    const dialog = this.dialog.open(EmailTemplateDialogComponent, {
      width: '1000px',
      height: '650px',
      data: {
        emailTemplate: template,
        emailSignature: signature
      }
    });

    dialog.afterClosed().pipe(take(1)).subscribe(
      (emailTemplate: { template: string, signature: string }) => {
        if (emailTemplate) {
          this.projectCommService.project.projectInfo = this.projectInfo;
          if (this.hasChecklistAccess) {
            this.projectCommService.projectInputDraftSaveNotifier.emit(true);
          }
          const project = this.projectCommService.getProject();
          if (!project.accountID) {
            project.accountID = localStorage.getItem('accountID');
          }

          if (project.projectInfo.isSubmittedProject) {
            project.projectInfo.isSubmittedProject = false;
            project.projectInfo.isDraftSubmission = true;
          }

          this.toastrService.info(this.translationService.get('PROJECT.INITIAL.SAVING_PROJ'));
          this.saveProjectToDatabase(project, this.translationService.get('PROJECT.INITIAL.SAVED_PROJ'), (savedProject: Project) => {
            this.snackBar.openFromComponent(ProgressSpinnerComponent, {
              data: this.translationService.get('PROJECT.INITIAL.SENDING'),
              panelClass: ['anessa-snack-bar-container'],
            });

            const body = {
              userID: localStorage.getItem('userID'),
              message: emailTemplate,
              project: savedProject,
            };
            this.commService.postRequest(body, '/wsasapi/project/send-link')
              .pipe(take(1))
              .pipe(takeUntil(this.onDestroyNotifier))
              .subscribe(() => {
                this.toastrService.success(this.translationService.get('PROJECT.INITIAL.SENT'));
                this.setup();
              });
          });
        }
      }
    );
  }

  onViewResults() {
    const tableHeaders = [
      this.translationService.get('RESULTS.GENERAL.NAME'),
      this.translationService.get('RESULTS.GENERAL.VALUE'),
      this.translationService.get('RESULTS.GENERAL.UNIT'),
    ];

    const dialogRef = this.dialog.open(InputReviewDialogComponent, {
      width: '65vw',
      height: '85vh',
      data: {
        isResults: true,
        tableHeaders: tableHeaders,
        results: this.projectCommService.getResults(),
      }
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe(approved => {
      if (approved && !Utils.limitToScreeningProjects()) {
        this.approveProject();
      }
    });
  }

  onSaveDraft() {
    this.projectCommService.project.projectInfo = this.projectInfo;
    if (!this.projectInfo.title || !this.projectCommService.checkUniqueProjectTitle(this.projectInfo.title)) {
      return;
    }
    if (this.hasChecklistAccess) {
      this.projectCommService.projectInputDraftSaveNotifier
        .pipe(take(1))
        .subscribe(save => {
          if (save === true) this.saveDraft();
        });
      this.projectCommService.projectInputDraftSaveNotifier.emit(true);
    } else {
      this.saveDraft();
    }
  }

  saveDraft() {
    const project = this.projectCommService.getProject();
    // Ensure default assumptions are set
    this.assumptionsService.setProjectAssumptions(project);
    this.projectCommService.setProject(project);

    project.projectInfo.isDraftSubmission = true;

    this.toastrService.info(this.translationService.get('PROJECT.INITIAL.SAVING_DRAFT'));
    this.saveProjectToDatabase(project, this.translationService.get('PROJECT.INITIAL.SAVED_DRAFT'));
  }

  onSubmit() {
    this.projectCommService.project.projectInfo = this.projectInfo;
    if (this.hasChecklistAccess) {
      this.projectCommService.projectInputDraftSaveNotifier
        .pipe(take(1))
        .subscribe(save => {
          if (save === true) {
            this.submitProject();
          }
        });
      this.projectCommService.projectInputDraftSaveNotifier.emit(true);
    } else {
      this.submitProject();
    }
  }

  submitProject() {
    this.projectCommService.checkCurrenciesUsed();
    const project = this.projectCommService.getProject();

    // Ensure default assumptions are set
    this.assumptionsService.setProjectAssumptions(project);
    this.projectCommService.setProject(project);

    const projectValidation = this.validationService.getProjectValidation();
    if (this.isInitialFormUser) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: { message: this.translationService.get('PROJECT.INITIAL.CONFIRM_SUBMIT_MESSAGE') }
      });

      dialogRef.afterClosed().pipe(take(1)).subscribe(status => {
        if (status) {
          this.saveSubmittedProject(project);
          this.pageDirty = false
        }
      });
    } else if (!projectValidation.complete && this.hasFormAccess && !this.isInitialFormUser) {
      const dialog = this.dialog.open(SubmissionProgressDialogComponent, {
        width: '800px',
        height: '85vh',
        data: projectValidation,
      });

      dialog.afterClosed().pipe(take(1)).subscribe((result: boolean) => {
        if (result) {
          this.saveSubmittedProject(project);
        }
      });
    } else {
      this.saveSubmittedProject(project);
      this.pageDirty = false;
    }

  }

  saveSubmittedProject(project) {
    project.projectInfo.isDraftSubmission = false;
    project.projectInfo.isSubmittedProject = true;

    this.toastrService.info(this.translationService.get('PROJECT.INITIAL.SUBMITTING'));
    this.saveProjectToDatabase(project, this.translationService.get('PROJECT.INITIAL.SUBMITTED'));
  }

  onSimulate() {
    this.projectCommService.checkCurrenciesUsed();
    const project = this.projectCommService.getProject();

    // Ensure default assumptions are set
    this.assumptionsService.setProjectAssumptions(project);
    this.projectCommService.setProject(project);
    const projectValidation = this.validationService.getProjectValidation();

    if (!projectValidation.complete) {
      this.dialog.open(ProgressPopupDialogComponent, {
        width: '800px',
        height: '85vh',
        data: {
          projectValidation: projectValidation,
          section: ValidationSectionName.All,
          onlyShowInvalid: true,
        }
      });
    } else {
      const tableHeaders = [
        this.translationService.get('RESULTS.GENERAL.NAME'),
        this.translationService.get('RESULTS.GENERAL.VALUE'),
        this.translationService.get('RESULTS.GENERAL.UNIT'),
      ];
      const titleTextList = [
        'PROJECT.INITIAL.REVIEW_DIALOG.INPUTS_LIST',
        'PROJECT.INITIAL.REVIEW_DIALOG.ASSUMPTIONS_LIST',
      ];
      const confirmTextList = [
        'PROJECT.INITIAL.REVIEW_DIALOG.ACCEPT_INPUTS',
        'PROJECT.INITIAL.REVIEW_DIALOG.ACCEPT_ASSUMPTIONS',
      ];

      // Save project before simulating
      this.toastrService.info(this.translationService.get('PROJECT.INITIAL.SAVING_PROJ'));
      this.saveProjectToDatabase(project, this.translationService.get('PROJECT.INITIAL.SAVED_PROJ'), () => {
        const inputSummary = this.projectCommService.getProject().getInputSummary(false, true);
        const projectAssumptions = this.assumptionsService.getProjectAssumptions();

        const dialogRef = this.dialog.open(InputReviewDialogComponent, {
          width: '65vw',
          height: '85vh',
          data: {
            titleText: titleTextList,
            confirmText: confirmTextList,
            tableHeaders: tableHeaders,
            tableData: [inputSummary, projectAssumptions],
          }
        });

        dialogRef.afterClosed().pipe(take(1)).subscribe(approved => {
          if (approved) {
            this.approveProject();
          }
        });
      });
    }
  }

  onAddMember() {
    const dialog = this.dialog.open(MemberComponent, {
      width: '600px',
      data: {
        member: null,
      }
    });

    dialog.afterClosed().pipe(take(1)).subscribe(
      (member: ProjectMember) => {
        if (member && member.name) {
          this.projectInfo.members.push(member);
          this.onSaveDraft();
        }
      }
    );
  }

  onEditMember(index: number) {
    const dialog = this.dialog.open(MemberComponent, {
      width: '600px',
      data: {
        member: ProjectMember.deepCopy(this.projectInfo.members[index]),
      }
    });

    dialog.afterClosed().pipe(take(1)).subscribe(
      (member: ProjectMember) => {
        if (member == null) {
          return;
        }
        this.projectInfo.members[index] = member;
        this.onSaveDraft();
      }
    );
  }

  onDeleteMember(index: number) {
    if (index != null && index >= 0) {
      this.projectInfo.members.splice(index, 1);
      this.onSaveDraft();
    }
  }

  onAddFile() {
    const dialog = this.dialog.open(AttachedFilesComponent, {
      width: '600px',
      data: {
        projectId: this.projectCommService.getProjectId(),
        file: null
      }
    });

    dialog.afterClosed().pipe(take(1)).subscribe(
      (file: ProjectFile) => {
        if (file && file.name) {
          this.projectInfo.attachedFiles.push(file);
          this.changesMade();
        }
      }
    );
  }

  onEditFile(index: number) {
    const dialog = this.dialog.open(AttachedFilesComponent, {
      width: '600px',
      data: {
        projectId: this.projectCommService.getProjectId(),
        file: ProjectFile.deepCopy(this.projectInfo.attachedFiles[index])
      }
    });

    dialog.afterClosed().pipe(take(1)).subscribe(
      (file: ProjectFile) => {
        if (file && file.name) {
          this.projectInfo.attachedFiles[index] = file;
          this.changesMade();
        }
      }
    );
  }

  onDeleteFile(index: number) {
    if (index != null && index >= 0) {
      this.filesToDelete.push(this.projectInfo.attachedFiles[index]);
      this.projectInfo.attachedFiles.splice(index, 1);
      this.changesMade();
    }
  }

  removeFiles() {
    // Remove any files the user deleted from the server now that the project is saved
    for (const file of this.filesToDelete) {
      const projectId = this.projectCommService.getProjectId();
      const fileId = file.fileId;
      const fileExt = file.fileExt;
      this.commService.deleteRequest(`${fileId}?ext=${fileExt}`, `/wsasapi/project-input/${projectId}/delete/`)
        .pipe(take(1))
        .pipe(takeUntil(this.onDestroyNotifier))
        .subscribe();
    }
  }

  changesMade() {
    // this.projectCommService.setProjectInfo(this.projectInfo);
    this.markDirty();
  }

  // Need to be like this for subcomponents or the plant spec slider breaks
  markDirty() {
    this.pageDirty = true;
  }

  showScopingResults() {
    this.router.navigateByUrl('/scoping-results');
  }

  useSavedTemplate() {
    this.dialog.open(ChecklistTemplateDialogComponent, {
      width: '1000px',
      data: {
        templates: this.checklistService.getChecklistFormTemplates(),
        templateType: 'form'
      }
    }).afterClosed()
      .pipe(take(1))
      .subscribe((result: { templateID: string, isTemplateDelete: boolean }) => {
        if (result && result.templateID) {
          if (!result.isTemplateDelete) {
            this.confirmAndLoadTemplate(result.templateID);
          } else if (result.isTemplateDelete) {
            this.checklistService.onFormTemplateDelete(result.templateID);
          }
        }
      });
  }

  confirmAndLoadTemplate(templateID) {
    let loadTemplate = () => {
      this.checklistService.loadFormTemplate(templateID).then(lists => this.projectInfo.checklists = lists);
      this.markDirty();
    };

    if (this.projectInfo.checklists.length > 0) {
      this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message: this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.IMPORT_DATA_WARNING'),
        }
      }).afterClosed()
        .pipe(take(1))
        .subscribe(proceed => {
          if (proceed) {
            loadTemplate();
          }
        });
    } else {
      loadTemplate();
    }
  }

  createNewTemplate() {
    this.router.navigate(['create-template'], { relativeTo: this.route });
    if (!this.creatingNewTemplate) {
      this.checklistService.projectInfo.checklists = [];
    }
  }
}
